import type { AxiosError, AxiosRequestConfig } from "axios";
import { ApiError, type ApiErrorResponse } from "./types";

const asNonEmptyString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0 ? value : undefined;

const extractRequestId = (headers: unknown) => {
  if (!headers || typeof headers !== "object") {
    return undefined;
  }

  const headerMap = headers as Record<string, unknown>;

  return (
    asNonEmptyString(headerMap["x-request-id"]) ??
    asNonEmptyString(headerMap["X-Request-Id"])
  );
};

const buildRequestUrl = (config?: AxiosRequestConfig) => {
  const url = asNonEmptyString(config?.url);
  const baseURL = asNonEmptyString(config?.baseURL);

  if (!url) {
    return baseURL;
  }

  if (!baseURL || /^https?:\/\//i.test(url)) {
    return url;
  }

  const trimmedBase = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
  return `${trimmedBase}${normalizedUrl}`;
};

export const buildApiErrorReportContext = (
  error: AxiosError<ApiErrorResponse>
) => ({
  mechanism: "api" as const,
  handled: true,
  tags: {
    http_method:
      asNonEmptyString(error.config?.method)?.toUpperCase() ?? "UNKNOWN",
  },
  extra: {
    method: asNonEmptyString(error.config?.method)?.toUpperCase(),
    url: buildRequestUrl(error.config),
    responseStatus: error.response?.status,
    responseData: error.response?.data,
  },
});

export const normalizeAxiosError = (error: AxiosError<ApiErrorResponse>) => {
  const payload = error.response?.data;
  const status = error.response?.status ?? payload?.statusCode;
  const message =
    asNonEmptyString(payload?.message) ??
    asNonEmptyString(payload?.errorMessage) ??
    asNonEmptyString(payload?.error) ??
    asNonEmptyString(error.message) ??
    "Something went wrong. Please try again later.";

  return new ApiError({
    message,
    status,
    data: payload ?? error.response?.data,
    errors: payload?.errors,
    errorCode: asNonEmptyString(payload?.errorCode),
    errorName: asNonEmptyString(payload?.errorName),
    requestId: extractRequestId(error.response?.headers),
  });
};

export const normalizeApiError = (error: unknown) => {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof Error) {
    return new ApiError({ message: error.message });
  }

  return new ApiError({
    message: "Unexpected error occurred. Please try again later.",
  });
};
